/**
 * 
 * @copyright     (c) 2025 Gist Applications Inc.
 * @author        Greg Olive greg@gist-apps.com
 * @package       gift-card-pro
 * 
 * /assets/js/React/Proxy/pages/GiftCardForm/index.tsx
 * Created:       Tue Jan 07 2025
 * Modified By:   Greg Olive
 * Last Modified: Wed Apr 23 2025
 */

import React, { useState, useEffect, useContext } from "react";
import { createPortal } from "react-dom";
import { dispatchSdKEvent,setSdkData } from "@root/_utils";
import { IFulfillmentType } from "@root/_types";
import { useBulkOrder, usePricingManager, useGiftCardFormManager, useGiftCardSettingsManager, useGiftCardStepManager, useGiftCardValidationManager, useGiftCardCurrencyManager } from "@root/_hooks";
import { TranslationsContext, FormContext, GlobalContext } from "@proxy/_contexts";
import { SubmitModal, RecordingModal, LoadingSkeleton, FormRenderer, CustomCSSElement } from "./_sections";
import getProxyValidationConfig from "./_utils/getProxyValidationConfig";
import { useParams } from "react-router";
import { FormValidator } from "../../../_utils";
import ProxyBulkOrderTable from "./_sections/ProxyBulkOrderTable";
import "@gistapps/composite-spreadsheet/dist/style.css";

declare global {
  interface Window {
      GCP:any;
      GCPSDK:any;
      Shopify:any;
  }
  
}


const getInitialType = (type: any, inDashboard: boolean): IFulfillmentType => {

  if (inDashboard) {
    return window.location.pathname.includes("physical") ? "physical" : "digital";
  }

  return type;

};

export default function GiftCardForm({
  preview,
  previewSettings,
  defaultStep,
  bulkOrder=false,
  inDashboard=false
}: any) {

  // Managing the main loading state outside of the hooks
  const [loading, setLoading] = useState(true);

  const { type } = useParams();

  const initialType = getInitialType(type, inDashboard);

  const globalVars = useContext(GlobalContext);

  // Keep physical state above all hooks, as it will drastically change the form depending on state.
  const [fulfillmentType, setFulfillmentType] = useState<IFulfillmentType>(initialType);

  const {
    settings,
    styleVars,
    translations,
    physicalEnabled,
    digitalEnabled,
  } = useGiftCardSettingsManager({ 
    previewSettings,
    preview, 
    inDashboard, 
    fulfillmentType 
  });

  const GCCURRENCY = useGiftCardCurrencyManager({ 
    inDashboard, 
    settings, 
    fulfillmentType
  });
  
  const GCVALIDATION = useGiftCardValidationManager({});
  
  const GCFORM = useGiftCardFormManager({ 
    pricingConfig: GCCURRENCY.pricingConfig,
    updateFormErrors: GCVALIDATION.updateFormErrors,
    loading,
    settings, 
    preview, 
    inDashboard,
    fulfillmentType,
    setFulfillmentType,
    globalVars,
  });
  

  const GCPRICING = usePricingManager({ 
    formData: GCFORM.formData,
    currency: GCCURRENCY.currency,
    pricingConfig: GCCURRENCY.pricingConfig,
    fulfillmentType,
    physicalEnabled,
    digitalEnabled,
    settings, 
    preview, 
    bulkOrder, 
    inDashboard
  });

  const GCSTEPS = useGiftCardStepManager({ 
    formData: GCFORM.formData,
    maxLinesReached: GCFORM.maxLinesReached,
    deliveryLeadTime: GCFORM.deliveryLeadTime,
    updateFormErrors: GCVALIDATION.updateFormErrors,
    physicalEnabled,
    digitalEnabled,
    defaultStep,
    bulkOrder, 
    getProxyValidationConfig,
    settings,
    translations,
    fulfillmentType,
    inDashboard
  });

  // The form does not need to be aware of bulk order
  const {
    bulkSpreadsheetRef,
    spreadsheetColumns,
    columnTypeConfig,
    handleCellActivated,
  } = useBulkOrder({
    settings,
    loading,
    bulkOrder,
    amountChoices: GCPRICING.amountChoices,
    pricingConfig: GCCURRENCY.pricingConfig,
    currency: GCCURRENCY.currency,
    updateFormField: GCFORM.updateFormField,
  });

  /**
   * Handle form submission
   */
  const handleOnSubmit = (event: Event, formData: any, validationConfig: any) => {

    const productAddToCart = window?.GCP?.productPage?.elements?.addToCart || null;

    if (GCSTEPS.validateAllSteps(formData, validationConfig)) {

      if (productAddToCart) {
        window.GCP.productPage.state.formValid = true;
        productAddToCart.click();
      } else {
        GCFORM.submitForm(event);
      }

    } else {

      if (productAddToCart) {
       window.GCP.productPage.state.formValid = false;
      }


    }

  }

  /**
   * Register global functions for product page actions
   * and bulk order actions.
   */
  const registerGlobals = () => {

    if (typeof(window.GCP) != 'undefined' && typeof(window.GCP.productPage) != 'undefined') {
      window.GCP.productPage.actions.handleOnSubmit = handleOnSubmit;
    }
  
  }

  useEffect(() => {

    const bothEnabled = physicalEnabled && digitalEnabled;

    if (!loading && !bothEnabled && !type) {
      
      GCSTEPS.handleNextStep(GCFORM.formData, GCSTEPS.validationConfig);

    }

  }, [type, physicalEnabled, digitalEnabled])

  /**
   * Handle global loading state
   */
  useEffect(() => {


    if (loading === false) return;

    if (Object.keys(GCFORM.formData).length === 0) {
      return;
    };

    if (!settings || (settings && settings.enable === false)) {
      return;
    };

    if (!GCPRICING.amountChoices.values) {
      return;
    };

    if (!GCCURRENCY.currency) {
      return;
    }

    // Critical step to ensure we always have a fullfillment type
    // on the first page.

    if (!fulfillmentType) {
      if (digitalEnabled && physicalEnabled) {
        setFulfillmentType("digital");
      } else if (physicalEnabled) {
        setFulfillmentType("physical");
      } else {
        setFulfillmentType("digital");
      }
    }

    setLoading(false);

    registerGlobals();

  }, [settings, GCFORM, GCPRICING, GCCURRENCY, fulfillmentType]);
  


  /**
   * Update the current step and line count when the step changes
   */
  useEffect(() => {
    GCFORM.updateLineCount();
    GCSTEPS.setCurrentStep(GCSTEPS.step);
    setSdkData('page', {
      'step': GCSTEPS.step,
    });
  }, [GCSTEPS.step]);

 
  /**
   * Trigger gcp-page-loaded event when the page is loaded if the preview is not enabled.
   */
  useEffect(() => {

    if (loading === true) return;

    if (settings.digital.appearance.enable_email_preview === "1" || settings.digital.appearance.enable_landing_preview === "1") return;

    dispatchSdKEvent('gcp-page-loaded');

  }, [loading]);

  /**
   * Trigger gcp-form-loaded event.
   */
  useEffect(() => {
    if (!loading) {
      dispatchSdKEvent('gcp-form-loaded');
    }
  }, [loading]);

  if (
    (loading === false && settings.enable === false ) ||
    (settings && settings.charge && settings.charge.balance_remaining <= 0 && settings.app_plan < 40)
  ) {
    return (
      <p>This page is currently unavailable. Please try again later</p>
    );
  }

  if (
    loading === false &&
    window.location.pathname.includes("a/gc/gift-card") &&
    (settings.global.appearance.form_theme === "product_template" && !bulkOrder) &&
    preview === false
  ) {
    window.location.replace(`https://${settings.shop.domain}${window.Shopify.routes.root}products/${settings.product.handle}`);
    return <></>;
  }

  window.GCPSDK.form.set = (field: string, data: any) => {
    GCFORM.updateFormField(field, data, true);
  };

  return (loading === true) ? <LoadingSkeleton /> : (
    <>
      <TranslationsContext.Provider
        value={translations && translations.purchase}
      >
      <FormContext.Provider
        value={{
          ...GCCURRENCY,
          ...GCVALIDATION,
          ...GCSTEPS,
          ...GCPRICING,
          ...GCFORM,
          physicalEnabled,
          digitalEnabled,
          fulfillmentType,
          setFulfillmentType,
          settings,
          previewSettings,
          styleVars,
          loading,
          preview,
          inDashboard,
          bulkOrder,
          validationConfig: getProxyValidationConfig(FormValidator, translations, settings, GCFORM.formData, GCFORM.maxLinesReached, GCFORM.deliveryLeadTime, fulfillmentType),
          setCurrentStep: GCSTEPS.goToStep,
          handleOnSubmit,
        }}
      >
        <CustomCSSElement
          preview={preview}
          settings={settings}
        />
        <FormRenderer
          bulkTranslations={translations && translations.bulk}
        />
        <RecordingModal/>
        <SubmitModal />
      </FormContext.Provider>
      </TranslationsContext.Provider>
      {(bulkOrder && spreadsheetColumns.length > 0) ? createPortal(
        <ProxyBulkOrderTable
          ref={bulkSpreadsheetRef}
          columns={spreadsheetColumns}
          columnTypeConfig={columnTypeConfig}
          onCellActivated={handleCellActivated}
        />,
        document.getElementById("gcpTableWrapper") || document.body,
      ) : null}
    </>
  );
};
